/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Papa from 'papaparse';

/* Import types */
import type { SensorCsvRow } from '@/types/sensorCsv';

export interface SensorMetadataEntry {
    address: string | null;
    anomaly: string | null;
    comments: string | null;
    customer: string | null;
    description: string;
    elevation: string | null;
    enabled: boolean;
    end: string | null;
    group: string | null;
    latitude: string | null;
    longitude: string | null;
    mapUrl: string | null;
    medium: string | null;
    name: string | null;
    persons: string | null;
    sensor: string;
    start: string | null;
    subgroup: string | null;
    system: string | null;
    trainEnd: string | null;
    trainStart: string | null;
    url: string | null;
}

export type SensorMetadataMap = Record<string, SensorMetadataEntry[]>;

interface CsvToSensorMetadataOptions {
    customer?: string;
    includeDisabled?: boolean;
}

function toOptionalText(value?: string): string | null {
    const trimmedValue = value?.trim();

    return trimmedValue ? trimmedValue : null;
}

function createGoogleMapsUrl(
    latitude: string | null,
    longitude: string | null,
): string | null {
    if (!latitude || !longitude) {
        return null;
    }

    return `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function stripPowerSuffix(sensorName: string): string {
    return sensorName.endsWith('.P') ? sensorName.slice(0, -2) : sensorName;
}

export function csvToSensorMetadata(
    csv: string,
    options: CsvToSensorMetadataOptions = {},
): SensorMetadataMap {
    const parsedCsv = Papa.parse<SensorCsvRow>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return parsedCsv.data.reduce<SensorMetadataMap>((accumulator, row) => {
        const includeDisabled = options.includeDisabled ?? false;

        if ((!includeDisabled && row.Enabled !== '1') || !row.Sensor) {
            return accumulator;
        }

        const meterKey = stripPowerSuffix(row.Sensor);
        const latitude = toOptionalText(row.Latitude);
        const longitude = toOptionalText(row.Longitude);
        const sensorMetadata: SensorMetadataEntry = {
            address: toOptionalText(row.Address),
            anomaly: toOptionalText(row.Anomaly),
            comments: toOptionalText(row.Comments),
            description: row.Description?.trim() ?? '',
            elevation: toOptionalText(row.Elevation),
            enabled: row.Enabled === '1',
            end: toOptionalText(row.End),
            group: toOptionalText(row.Group),
            latitude,
            longitude,
            mapUrl: createGoogleMapsUrl(latitude, longitude),
            medium: toOptionalText(row.Medium),
            name: toOptionalText(row.Name),
            persons: toOptionalText(row.Persons),
            sensor: row.Sensor.trim(),
            start: toOptionalText(row.Start),
            subgroup: toOptionalText(row.Subgroup),
            system: toOptionalText(row.System),
            trainEnd: toOptionalText(row.Trainend),
            trainStart: toOptionalText(row.Trainstart),
            url: toOptionalText(row.URL),
            customer: toOptionalText(options.customer),
        };

        if (!accumulator[meterKey]) {
            accumulator[meterKey] = [];
        }

        accumulator[meterKey].push(sensorMetadata);

        return accumulator;
    }, {});
}

export function getCsvSensorSystems(csv: string): string[] {
    const parsedCsv = Papa.parse<SensorCsvRow>(csv, {
        delimiter: ';',
        header: true,
        skipEmptyLines: true,
    });

    return Array.from(
        new Set(
            parsedCsv.data
                .map((row) => toOptionalText(row.System))
                .filter((system): system is string => system !== null),
        ),
    ).sort((leftSystem, rightSystem) => leftSystem.localeCompare(rightSystem));
}
